<script setup>
/**
 * Drawings of the app for the feature panels, used where there is no
 * screenshot that fits the page. Built from tokens, so they take on whichever
 * theme the page is in.
 *
 * Decoration only: the panel's copy carries the meaning, so the whole
 * drawing is hidden from screen readers. Sample names and prices are
 * illustrations, not claims.
 */
import IconBell from "../icons/IconBell.vue";
import IconCalendar from "../icons/IconCalendar.vue";
import IconClock from "../icons/IconClock.vue";
import IconPhone from "../icons/IconPhone.vue";

defineProps({
  // booking | reminders | business
  visual: { type: String, required: true },
});

const services = [
  { name: "תספורת גבר", meta: "30 דק׳" },
  { name: "תספורת + זקן", meta: "45 דק׳", active: true },
  { name: "לק ג׳ל", meta: "60 דק׳" },
];

const days = [
  { day: "ד׳", date: 5 },
  { day: "ה׳", date: 6, active: true },
  { day: "ו׳", date: 7 },
  { day: "א׳", date: 9 },
];

const times = [
  { time: "10:00", taken: true },
  { time: "10:45" },
  { time: "11:00", active: true },
  { time: "11:45" },
  { time: "12:30", taken: true },
  { time: "13:15" },
];

const products = [
  { name: "שמן לזקן", price: "₪59", tone: "brand" },
  { name: "ערכת טיפוח", price: "₪120", tone: "accent" },
  { name: "לק ג׳ל", price: "₪45", tone: "soft" },
];
</script>

<template>
  <div class="art" :class="`art--${visual}`" aria-hidden="true">
    <!-- ------------------------------------------------------- booking -->
    <div v-if="visual === 'booking'" class="screen">
      <p class="screen__title">קביעת תור</p>

      <ul class="services">
        <li
          v-for="s in services"
          :key="s.name"
          class="service"
          :class="{ 'is-active': s.active }"
        >
          <span>{{ s.name }}</span>
          <span class="service__meta">{{ s.meta }}</span>
        </li>
      </ul>

      <div class="days">
        <span
          v-for="d in days"
          :key="d.date"
          class="day"
          :class="{ 'is-active': d.active }"
        >
          <span class="day__name">{{ d.day }}</span>
          <strong>{{ d.date }}</strong>
        </span>
      </div>

      <div class="times">
        <span
          v-for="t in times"
          :key="t.time"
          class="time"
          :class="{ 'is-taken': t.taken, 'is-active': t.active }"
        >
          {{ t.time }}
        </span>
      </div>

      <span class="screen__cta">קביעת תור · ה׳ 11:00</span>
    </div>

    <!-- ----------------------------------------------------- reminders -->
    <div v-else-if="visual === 'reminders'" class="lock">
      <p class="lock__time">09:41</p>
      <p class="lock__date">יום חמישי, 6 בנובמבר</p>

      <div class="push">
        <span class="push__app"><IconBell /></span>
        <span class="push__body">
          <span class="push__head">
            <strong>תזכורת לתור</strong>
            <span>עכשיו</span>
          </span>
          <span>תספורת + זקן היום ב-11:00. נתראה!</span>
        </span>
      </div>

      <div class="push push--back">
        <span class="push__app push__app--accent"><IconCalendar /></span>
        <span class="push__body">
          <span class="push__head">
            <strong>התפנה תור</strong>
            <span>לפני 5 דק׳</span>
          </span>
          <span>לק ג׳ל · היום 16:30</span>
        </span>
      </div>
    </div>

    <!-- ------------------------------------------------------ business -->
    <div v-else-if="visual === 'business'" class="screen screen--flush">
      <div class="cover">
        <span class="cover__name">סלון מאיה</span>
        <span class="cover__place">חיפה</span>
      </div>

      <div class="info">
        <span class="info__row">
          <IconClock />
          <span>א׳–ה׳ 09:00–19:00 · ו׳ עד 14:00</span>
        </span>
        <span class="info__row">
          <IconPhone />
          <span dir="ltr">04-•••-••12</span>
        </span>
      </div>

      <p class="screen__label">מוצרים</p>
      <div class="products">
        <span
          v-for="p in products"
          :key="p.name"
          class="product"
        >
          <span class="product__img" :class="`product__img--${p.tone}`" />
          <span class="product__name">{{ p.name }}</span>
          <strong>{{ p.price }}</strong>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.art {
  inline-size: min(100%, 340px);
}

/* A phone screen without the phone: one rounded card. */
.screen {
  display: grid;
  gap: var(--space-3);
  padding: var(--space-5);
  border: var(--border);
  border-radius: var(--radius-2xl);
  background: var(--surface);
  box-shadow: var(--lift-3);
}

.screen--flush {
  padding: 0 0 var(--space-5);
  overflow: hidden;
}

.screen__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
  color: var(--ink);
}

.screen__label {
  margin: 0;
  padding-inline: var(--space-5);
  font-size: var(--text-sm);
  font-weight: var(--weight-bold);
  color: var(--ink);
}

.screen__cta {
  padding-block: var(--space-3);
  border-radius: var(--radius-pill);
  background: var(--brand);
  color: var(--on-brand);
  font-size: var(--text-sm);
  font-weight: var(--weight-bold);
  text-align: center;
}

/* -------------------------------------------------------------- booking */

.services {
  display: grid;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.service {
  display: flex;
  justify-content: space-between;
  padding: var(--space-2) var(--space-3);
  border: var(--border);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  color: var(--ink);
}

.service__meta {
  color: var(--ink-subtle);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
}

.service.is-active {
  border-color: var(--brand);
  background: var(--brand-softer);
  font-weight: var(--weight-semibold);
}

.service.is-active .service__meta {
  color: var(--brand-ink);
}

.days {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-2);
}

.day {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-block: var(--space-2);
  border-radius: var(--radius-md);
  background: var(--canvas);
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.day__name {
  font-size: var(--text-xs);
  color: var(--ink-subtle);
}

.day.is-active {
  background: var(--ink);
  color: var(--on-ink);
}

.day.is-active .day__name {
  color: var(--on-ink-muted);
}

.times {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
}

.time {
  padding-block: var(--space-2);
  border: var(--border);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  text-align: center;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.time.is-taken {
  color: var(--ink-subtle);
  background: var(--canvas);
  text-decoration: line-through;
}

.time.is-active {
  border-color: var(--brand);
  background: var(--brand);
  color: var(--on-brand);
}

/* ------------------------------------------------------------ reminders */

.lock {
  display: grid;
  gap: var(--space-3);
  justify-items: center;
  padding: var(--space-7) var(--space-4) var(--space-8);
  border-radius: var(--radius-2xl);
  background: radial-gradient(
      90% 70% at 100% 0%,
      var(--brand-glow),
      transparent 60%
    ),
    radial-gradient(80% 60% at 0% 100%, var(--brand-glow-soft), transparent 60%),
    var(--ink);
  box-shadow: var(--lift-3);
}

.lock__time {
  margin: 0;
  font-size: 3.25rem;
  font-weight: var(--weight-semibold);
  line-height: 1;
  color: var(--on-ink);
  font-variant-numeric: tabular-nums;
}

.lock__date {
  margin: 0 0 var(--space-4);
  font-size: var(--text-sm);
  color: var(--on-ink-muted);
}

.push {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  inline-size: 100%;
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--lift-2);
}

.push--back {
  opacity: 0.85;
  transform: scale(0.96);
}

.push__app {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  inline-size: 34px;
  block-size: 34px;
  border-radius: var(--radius-sm);
  background: var(--brand);
  color: var(--on-brand);
}

.push__app--accent {
  background: var(--accent);
}

.push__app :deep(svg) {
  inline-size: 18px;
  block-size: 18px;
}

.push__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-inline-size: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-snug);
  color: var(--ink-muted);
}

.push__head {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
}

.push__head strong {
  color: var(--ink);
}

.push__head span {
  font-size: var(--text-xs);
  color: var(--ink-subtle);
}

/* ------------------------------------------------------------- business */

.cover {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-block-size: 130px;
  padding: var(--space-4) var(--space-5);
  background: linear-gradient(200deg, var(--brand-glow), transparent 70%),
    linear-gradient(160deg, var(--ink-2), var(--ink));
}

.cover__name {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  color: var(--on-ink);
}

.cover__place {
  font-size: var(--text-sm);
  color: var(--on-ink-muted);
}

.info {
  display: grid;
  gap: var(--space-2);
  margin-inline: var(--space-5);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--canvas);
}

.info__row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--ink);
}

.info__row :deep(svg) {
  inline-size: 16px;
  block-size: 16px;
  flex-shrink: 0;
  color: var(--brand);
}

.products {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
  padding-inline: var(--space-5);
}

.product {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--text-xs);
  color: var(--ink);
}

.product__name {
  color: var(--ink-muted);
}

.product__img {
  block-size: 64px;
  margin-block-end: var(--space-1);
  border-radius: var(--radius-md);
}

.product__img--brand {
  background: linear-gradient(160deg, var(--brand-soft), var(--brand));
}

.product__img--accent {
  background: linear-gradient(160deg, var(--accent-soft), var(--accent));
}

.product__img--soft {
  background: linear-gradient(160deg, var(--surface), var(--line-strong));
}
</style>
